import { type KeyboardEvent, useRef } from 'react'

export interface PickerKeyboardOptions {
  open: boolean
  disabled?: boolean | undefined
  /** The search as last rendered. */
  search: string
  onSearchChange: (search: string) => void
  onOpenChange: (open: boolean) => void
  /**
   * Enter reached the field while the list was opening (a scanner types the code and Enter faster
   * than the list renders): handle it as Enter in the search box, given everything typed so far.
   */
  onEnter?: (typed: string) => void
}

/**
 * The keyboard contract every picker shares (Combobox, AsyncCombobox, the product picker): typing
 * on the closed field opens the list with that text as the search, and no keystroke is lost on the
 * way. The first key seeds the search; keys that still reach the field before the search box has
 * taken focus are added to it (a barcode scanner's burst, Enter included); and when the search box
 * takes focus its caret goes to the end, nothing selected, so the next key adds to the search
 * instead of replacing it (Radix focuses with `select: true` by default).
 *
 * Spread `onTriggerKeyDown` on the trigger, `onOpenAutoFocus` on `Popover.Content` and `inputRef`
 * on the search input.
 */
export function usePickerKeyboard({
  open,
  disabled,
  search,
  onSearchChange,
  onOpenChange,
  onEnter,
}: PickerKeyboardOptions) {
  const inputRef = useRef<HTMLInputElement>(null)
  // What has been typed so far, ahead of the render that shows it (keys can come faster).
  const typed = useRef(search)
  typed.current = search

  const type = (next: string) => {
    typed.current = next
    onSearchChange(next)
  }

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (disabled || event.ctrlKey || event.metaKey || event.altKey) return
    const character = event.key.length === 1
    if (!open) {
      if (character && event.key !== ' ') {
        event.preventDefault()
        type(event.key)
        onOpenChange(true)
      } else if (event.key === 'ArrowDown') {
        event.preventDefault()
        onOpenChange(true)
      }
      return
    }
    // Open, but the search box has not taken focus yet: the keys belong to the search.
    if (character) {
      event.preventDefault()
      type(typed.current + event.key)
    } else if (event.key === 'Backspace') {
      event.preventDefault()
      type(typed.current.slice(0, -1))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      onEnter?.(typed.current)
    }
  }

  /** Focus the search box with the caret after what was typed, instead of selecting it. */
  const onOpenAutoFocus = (event: Event) => {
    const input = inputRef.current
    if (!input) return
    event.preventDefault()
    input.focus({ preventScroll: true })
    const end = input.value.length
    input.setSelectionRange(end, end)
  }

  return { inputRef, onTriggerKeyDown, onOpenAutoFocus }
}
